import { IconDotsVertical, IconMessageCircle, IconTrash } from '@tabler/icons-react';
import { INFLUENCER_CONTENT_TYPE_ENUM } from '@Saral-Influencer-Marketing/saral-utils/dist/types/models/influencerContent';
import { calculateImageColor } from '@Saral-Influencer-Marketing/saral-utils/src/functions/image';
import { Popover } from '@mantine/core';
import { useState } from 'react';
import EditIcon from '../../../../assets/icons/edit-icon';

import TrashIcon from '../../../../assets/icons/trash-icon';
import { ContentSubmissionStage, ContentSubmissionType } from '../../../../types/content-submissions';
import { Button } from '../../../styledComponents';
import './submission-card.css';
import ButtonComponent from '../../../common/button-component/button-component';
import ResubmissionTag from '../resubmission-tag/resubmission-tag';
import VideoThumbnail from '../../../common/video-thumbnail/video-thumbnail';
import CustomTooltip from '../../../common/CustomTooltip/CustomTooltip';
import CommentIcon from '../../../../assets/icons/CommentIcon';

interface SubmissionCardProps {
  file: string;
  type: string;
  name: string;
  date: string;
  stage: ContentSubmissionStage;
  commentCount?: number;
  submissionType: ContentSubmissionType;
  onView: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
  isResubmission?: boolean;
}

export default function SubmissionCard({
  file,
  type,
  name,
  date,
  stage,
  commentCount = 0,
  submissionType,
  onView,
  onEdit,
  onDelete,
  isResubmission,
}: SubmissionCardProps) {
  const [showDropdown, setShowDropdown] = useState(false);
  const [thumbnailColor, setThumbnailColor] = useState<string>('transparent');

  const getThumbnailSrc = () => {
    // For image files
    if (type && type.includes('image') && submissionType === INFLUENCER_CONTENT_TYPE_ENUM.MEDIA) {
      return file;
    }
    // For link submissions
    if (submissionType === INFLUENCER_CONTENT_TYPE_ENUM.LINK) {
      return '/images/content-submission/link-placeholder.png';
    }
    // Default placeholder
    return '/images/content-submission/media-placeholder.jpeg';
  };

  const renderActions = () => {
    switch (stage) {
      case 'pending':
        return (
          <div className="submission-actions pending-submission-actions">
            {onEdit && (
              <CustomTooltip title="Edit Content">
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEdit();
                  }}
                  aria-label="Edit submission"
                  type="button"
                >
                  <EditIcon />
                </Button>
              </CustomTooltip>
            )}
            {onDelete && (
              <CustomTooltip title="Remove Content">
                <Button
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete();
                  }}
                  aria-label="Delete submission"
                  type="button"
                >
                  <TrashIcon />
                </Button>
              </CustomTooltip>
            )}
          </div>
        );
      case 'changesRequested':
        return (
          <div className="submission-actions">
            <div className="comment-count">
              <CommentIcon width={18} height={18} color="var(--icon-subdued)" />
              <span>{commentCount}</span>
            </div>
            <div className="dropdown-container">
              <Popover
                opened={Boolean(showDropdown)}
                onClose={() => {
                  setShowDropdown(false);
                }}
                withArrow
                styles={{
                  dropdown: {
                    padding: 0,
                    border: 'none',
                  },
                  arrow: {
                    border: 'none',
                  },
                }}
              >
                <Popover.Target>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowDropdown(!showDropdown);
                    }}
                    className={`action-button ${showDropdown ? 'action-button-active' : ''}`}
                    aria-label="More options"
                    type="button"
                  >
                    <IconDotsVertical size={18} />
                  </button>
                </Popover.Target>
                <Popover.Dropdown>
                  <div className="dropdown-menu">
                    {onDelete && (
                      <button
                        onClick={() => {
                          onDelete();
                          setShowDropdown(false);
                        }}
                        type="button"
                      >
                        <IconTrash size={18} />
                        Delete Submission
                      </button>
                    )}
                  </div>
                </Popover.Dropdown>
              </Popover>
            </div>
          </div>
        );
      case 'approved':
        return (
          <div className="submission-actions">
            <div className="comment-count">
              <IconMessageCircle size={18} />
              <span>{commentCount}</span>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const getThumbnailColor = async (imageElement: HTMLImageElement) => {
    let color = 'transparent';

    if (imageElement) {
      const getColor = await calculateImageColor(imageElement as HTMLImageElement, {
        algorithm: 'sqrt',
      });
      if (getColor) {
        color = getColor;
      }
    }
    setThumbnailColor(color);
  };

  return (
    <div role="button" tabIndex={0} className="submission-card" onClick={onView}>
      <div className="submission-thumbnail" style={{ backgroundColor: thumbnailColor }}>
        {type && type.includes('video') && submissionType === INFLUENCER_CONTENT_TYPE_ENUM.MEDIA ? (
          <VideoThumbnail videoUrl={file} alt={name} className="thumbnail-image" />
        ) : (
          <img
            src={getThumbnailSrc()}
            alt={name}
            crossOrigin="anonymous"
            onLoad={(e) => getThumbnailColor(e.target as HTMLImageElement)}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              target.src = '/images/content-submission/media-placeholder.jpeg';
            }}
          />
        )}
      </div>
      {isResubmission && <ResubmissionTag className="thumbnail-tag" />}

      <div className="submission-content">
        <div className="submission-info">
          <h3 className="submission-title">{name}</h3>
          {renderActions()}
        </div>
        <p className="submission-date">{date}</p>
      </div>

      <ButtonComponent onClick={onView} className="view-button" type="button">
        View
      </ButtonComponent>
    </div>
  );
}
